/**
 * RIG-381 — offline ZIP → US state + ZIP → IANA timezone lookups.
 *
 * These power a *smart, overridable default* on data-entry forms (account /
 * lead / meeting): the rep types a ZIP and we fill the state + timezone they'd
 * otherwise pick by hand. They are NOT an authoritative geocoder — a full ZIP
 * dataset / geocoding service is deliberately out of scope (overkill for a
 * US-only, 7-zone field + 2-letter state).
 *
 * Design:
 *  - No network, no runtime dependency, fully synchronous.
 *  - `stateForZip` is exact: US ZIPs map to states by well-defined 3-digit
 *    prefix ranges.
 *  - `timezoneForZip` resolves to one of the seven curated US zones
 *    (see `US_TIMEZONES`). Single-timezone states resolve directly; the
 *    handful of split-timezone states encode their known sub-ranges. When a
 *    prefix is genuinely ambiguous (or non-US/unknown) BOTH return `null` so
 *    the caller leaves the field for the user rather than guessing wrong.
 */
/** Normalize free-form input to a 5-digit ZIP, or null if it isn't one. */
function normalizeZip(zip) {
    if (!zip)
        return null;
    const digits = zip.trim().replace(/[^0-9]/g, '');
    // Accept ZIP or ZIP+4; the state/zone live in the first five.
    if (digits.length < 5)
        return null;
    return digits.slice(0, 5);
}
/**
 * QA 2026-10-01 F-04 — the stored form of a US ZIP.
 *
 * Accepts `12345`, `12345-6789`, `123456789` and `12345 6789` (surrounding
 * whitespace ignored). Returns `12345` or `12345-6789`, or null when the input
 * is not a 5- or 9-digit ZIP ("abcde", "1234", "123456"). Blank returns null;
 * callers decide whether blank means "leave empty".
 *
 * Distinct from the lenient `normalizeZip` above, which only needs the first
 * five digits for a lookup default and never rejects.
 */
export function normalizeUsZip(input) {
    if (typeof input !== 'string')
        return null;
    const m = /^(\d{5})(?:[-\s]?(\d{4}))?$/.exec(input.trim());
    if (!m)
        return null;
    return m[2] ? `${m[1]}-${m[2]}` : m[1];
}
/** Field message for an invalid ZIP, shared by the server and the forms. */
export const US_ZIP_INVALID_MESSAGE = 'Use a 5-digit ZIP code, or ZIP+4 like 35203-1234.';
/** First-3-digit prefix as a number (021 → 21), for range comparisons. */
function prefixOf(zip5) {
    return Math.floor(Number(zip5) / 100);
}
// 3-digit-prefix ranges → 2-letter USPS state. Ordered, non-overlapping.
// Covers the 50 states + DC; territories (PR/VI/GU) resolve to their code but
// have no curated timezone (→ null from timezoneForZip).
const STATE_RANGES = [
    [5, 5, 'NY'],
    [6, 9, 'PR'],
    [10, 27, 'MA'],
    [28, 29, 'RI'],
    [30, 38, 'NH'],
    [39, 49, 'ME'],
    [50, 54, 'VT'],
    [55, 55, 'MA'],
    [56, 59, 'VT'],
    [60, 69, 'CT'],
    [70, 89, 'NJ'],
    [100, 149, 'NY'],
    [150, 196, 'PA'],
    [197, 199, 'DE'],
    [200, 205, 'DC'],
    [206, 219, 'MD'],
    [220, 246, 'VA'],
    [247, 268, 'WV'],
    [270, 289, 'NC'],
    [290, 299, 'SC'],
    [300, 319, 'GA'],
    [320, 339, 'FL'],
    [340, 349, 'FL'],
    [350, 369, 'AL'],
    [370, 385, 'TN'],
    [386, 397, 'MS'],
    [398, 399, 'GA'],
    [400, 427, 'KY'],
    [430, 459, 'OH'],
    [460, 479, 'IN'],
    [480, 499, 'MI'],
    [500, 528, 'IA'],
    [530, 549, 'WI'],
    [550, 567, 'MN'],
    [570, 577, 'SD'],
    [580, 588, 'ND'],
    [590, 599, 'MT'],
    [600, 629, 'IL'],
    [630, 658, 'MO'],
    [660, 679, 'KS'],
    [680, 693, 'NE'],
    [700, 714, 'LA'],
    [716, 729, 'AR'],
    [730, 749, 'OK'],
    [750, 799, 'TX'],
    [800, 816, 'CO'],
    [820, 831, 'WY'],
    [832, 838, 'ID'],
    [840, 847, 'UT'],
    [850, 860, 'AZ'],
    [863, 865, 'AZ'],
    [870, 884, 'NM'],
    [885, 885, 'TX'],
    [889, 898, 'NV'],
    [900, 961, 'CA'],
    [967, 968, 'HI'],
    [970, 979, 'OR'],
    [980, 994, 'WA'],
    [995, 999, 'AK'],
];
/** 2-letter USPS state code for a US ZIP, or null if unmappable. */
export function stateForZip(zip) {
    const zip5 = normalizeZip(zip);
    if (!zip5)
        return null;
    const p = prefixOf(zip5);
    for (const [lo, hi, state] of STATE_RANGES) {
        if (p >= lo && p <= hi)
            return state;
    }
    return null;
}
// States that sit entirely in one of the seven curated zones.
const SINGLE_ZONE_STATE = {
    // Eastern
    CT: 'America/New_York', DE: 'America/New_York', DC: 'America/New_York',
    GA: 'America/New_York', ME: 'America/New_York', MD: 'America/New_York',
    MA: 'America/New_York', NH: 'America/New_York', NJ: 'America/New_York',
    NY: 'America/New_York', NC: 'America/New_York', OH: 'America/New_York',
    PA: 'America/New_York', RI: 'America/New_York', SC: 'America/New_York',
    VT: 'America/New_York', VA: 'America/New_York', WV: 'America/New_York',
    // Central
    AL: 'America/Chicago', AR: 'America/Chicago', IA: 'America/Chicago',
    IL: 'America/Chicago', LA: 'America/Chicago', MN: 'America/Chicago',
    MS: 'America/Chicago', MO: 'America/Chicago', OK: 'America/Chicago',
    WI: 'America/Chicago', ND: 'America/Chicago',
    // Mountain
    CO: 'America/Denver', MT: 'America/Denver', NM: 'America/Denver',
    UT: 'America/Denver', WY: 'America/Denver',
    // Mountain, no-DST
    AZ: 'America/Phoenix',
    // Pacific
    CA: 'America/Los_Angeles', WA: 'America/Los_Angeles', NV: 'America/Los_Angeles',
    // Alaska / Hawaii
    AK: 'America/Anchorage', HI: 'Pacific/Honolulu',
};
/**
 * Best-effort timezone for the split-timezone states, by 3-digit prefix.
 * Returns a zone for the well-established sub-ranges, or null where the prefix
 * straddles a boundary we can't resolve confidently (caller leaves it blank).
 */
function splitStateZone(state, prefix) {
    switch (state) {
        case 'FL': // Panhandle (Pensacola/Panama City) is Central; rest Eastern.
            return prefix === 324 || prefix === 325 ? 'America/Chicago' : 'America/New_York';
        case 'TX': // El Paso/Hudspeth are Mountain; rest Central.
            return prefix === 798 || prefix === 799 || prefix === 885
                ? 'America/Denver'
                : 'America/Chicago';
        case 'KY': // Louisville/Lexington (≤418) Eastern; western KY (≥420) Central.
            return prefix <= 418 ? 'America/New_York' : 'America/Chicago';
        case 'IN': // Gary (463–464) + Evansville (476–477) Central; rest Eastern.
            return prefix === 463 || prefix === 464 || prefix === 476 || prefix === 477
                ? 'America/Chicago'
                : 'America/New_York';
        case 'MI': // Western UP (498–499) Central; rest Eastern.
            return prefix === 498 || prefix === 499 ? 'America/Chicago' : 'America/New_York';
        case 'SD': // Western SD (≥576) Mountain; eastern Central.
            return prefix >= 576 ? 'America/Denver' : 'America/Chicago';
        case 'NE': // Panhandle (≥691) Mountain; rest Central.
            return prefix >= 691 ? 'America/Denver' : 'America/Chicago';
        case 'KS': // Far-western KS (678–679) Mountain; rest Central.
            return prefix === 678 || prefix === 679 ? 'America/Denver' : 'America/Chicago';
        case 'ID': // Panhandle (838) Pacific; rest Mountain.
            return prefix === 838 ? 'America/Los_Angeles' : 'America/Denver';
        case 'OR': // Malheur County (979) Mountain; rest Pacific.
            return prefix === 979 ? 'America/Denver' : 'America/Los_Angeles';
        case 'TN': // Memphis/Nashville Central; Knoxville/Chattanooga Eastern;
            // the middle (373/375) straddles the line → leave blank.
            if (prefix === 370 || prefix === 371 || prefix === 372 || prefix === 380 || prefix === 381 || prefix === 382 || prefix === 383 || prefix === 384 || prefix === 385) {
                return 'America/Chicago';
            }
            if (prefix === 374 || prefix === 376 || prefix === 377 || prefix === 378 || prefix === 379) {
                return 'America/New_York';
            }
            return null;
        default:
            return null;
    }
}
/**
 * Curated US IANA timezone for a ZIP, or null when unmappable / ambiguous.
 * The result is one of `US_TIMEZONES`; callers treat it as an overridable
 * default, never an authority.
 */
export function timezoneForZip(zip) {
    const zip5 = normalizeZip(zip);
    if (!zip5)
        return null;
    const state = stateForZip(zip5);
    if (!state)
        return null;
    const single = SINGLE_ZONE_STATE[state];
    if (single)
        return single;
    return splitStateZone(state, prefixOf(zip5));
}
//# sourceMappingURL=zip.js.map